/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { track } from '@/lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();

  const [status, setStatus] = useState('checking'); // checking | paid | pending | failed | expired
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      setError('Missing session ID.');
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/api/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetail(data);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          track('purchase', {
            transaction_id: sessionId,
            currency: (data.currency || 'usd').toUpperCase(),
            value: Number(data.amount_total || data.amount || 0),
            items: [{
              item_id: data.product_id,
              item_name: data.product_name,
              size: data.size,
            }],
          });
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus('pending');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setError('Could not verify the payment yet.');
        if (attempts >= MAX_POLLS) {
          setStatus('failed');
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FFF7EE] text-black flex items-center justify-center px-6 py-16">
      <div className="max-w-xl w-full bg-white border-2 border-black rounded-3xl shadow-[10px_10px_0_#FF6600] p-10 text-center">
        <Link to="/" className="inline-block mb-8">
          <img src="/brand/logo-mark.png" alt="Shirt Happens Studio" className="h-12 w-auto mx-auto" draggable="false" />
        </Link>

        {status === 'checking' && (
          <>
            <h1 className="font-display font-black text-3xl mb-3">Confirming your order…</h1>
            <p className="font-body text-black/70">Hang tight — pulling the receipt from Stripe.</p>
            <div className="mt-6 w-12 h-12 rounded-full border-4 border-black/10 border-t-brand-orange animate-spin mx-auto" />
          </>
        )}

        {status === 'paid' && (
          <>
            <p className="font-body text-xs tracking-[0.4em] uppercase text-brand-orange mb-3">
              Shirt happened.
            </p>
            <h1 className="font-display font-black text-4xl mb-4">Order confirmed.</h1>
            <p className="font-body text-black/70 mb-6">
              Thanks for picking up <strong>{detail?.product_id || 'a Shirt Happens tee'}</strong>
              {detail?.size ? <> in size <strong>{detail.size}</strong></> : null}. You'll get a confirmation email
              from Stripe in a moment. Print + ship typically takes 3 to 7 days.
            </p>
            {(() => {
              const meta = detail?.metadata || {};
              const subtotal = parseFloat(meta.subtotal || 0);
              const taxAmount = parseFloat(meta.tax_amount || 0);
              const total = Number(detail?.amount_total || detail?.amount || 0);
              if (!subtotal || !taxAmount) return null;
              return (
                <div className="mb-6 text-left bg-black/5 rounded-2xl p-4 border border-black/10">
                  <div className="font-display font-black text-[10px] tracking-[0.32em] uppercase text-black/50 mb-2 text-center">Receipt</div>
                  <div className="flex justify-between text-sm text-black/70 mb-1">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-black/70 mb-2">
                    <span>TX Sales Tax (8.25%)</span>
                    <span>${taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-base font-display font-black text-black pt-2 border-t border-black/10">
                    <span>Total charged</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 font-display font-bold text-sm uppercase tracking-wide bg-black text-white rounded-full px-6 py-3 hover:bg-brand-orange transition-colors"
            >
              Back to the studio →
            </button>
          </>
        )}

        {status === 'pending' && (
          <>
            <h1 className="font-display font-black text-3xl mb-3">Still processing…</h1>
            <p className="font-body text-black/70">
              Your payment is still being processed. We've saved your order — you'll get an email
              confirmation as soon as Stripe finalizes it.
            </p>
          </>
        )}

        {status === 'expired' && (
          <>
            <h1 className="font-display font-black text-3xl mb-3">Session expired</h1>
            <p className="font-body text-black/70 mb-6">No charge was made. Want to try again?</p>
            <button
              type="button"
              onClick={() => navigate('/#shop')}
              className="inline-flex items-center gap-2 font-display font-bold text-sm uppercase tracking-wide bg-black text-white rounded-full px-6 py-3 hover:bg-brand-orange transition-colors"
            >
              Back to the shop →
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <h1 className="font-display font-black text-3xl mb-3">Hmm, something's off.</h1>
            <p className="font-body text-black/70 mb-6">{error || 'We could not verify the order right now.'}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 font-display font-bold text-sm uppercase tracking-wide bg-black text-white rounded-full px-6 py-3 hover:bg-brand-orange transition-colors"
            >
              Back home →
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
